import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Radio, notification } from 'antd';
import { ArrowsClockwise, CheckCircle, Warning, X } from '@phosphor-icons/react';
import { useOneButtonStore } from '../../../../store/oneButtonStore';
import opportunityInfoServices from '../../../../services/oneButtonServices/opportunityInfoService';
import {
    toTitleUseCode,
    aspNetValidationMessage,
    opportunityContactNumericId,
    isBlackKnightSyncedResponse,
    extractTitleSearchOwners,
} from '../../../../helpers/titleSearchOwners';
import { refreshOpportunityAfterTitleSync } from '../../../../helpers/titleContactSyncRefresh';
import DashboardModal from './DashboardModal';
import { OBDSelect } from './form-elements';
import './oneButton-css/AddTitleContactsModal.css';
import './oneButton-css/ObdSoftGlassOverlays.css';

interface BlackKnightLeadModalProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    data?: any; 
    onSuccess?: () => void;
}

const ContactTableRow: React.FC<{
    row: any; theme: string; index: number; isBK: boolean;
    existingContacts: { label: string; value: string }[];
    updateRow: (index: number, updates: Partial<any>) => void;
}> = React.memo(({ row, theme, index, isBK, existingContacts, updateRow }) => (
    <tr className="obd-atc-modal__tr">
        <td className="obd-atc-modal__td">{row.firstName}</td>
        <td className="obd-atc-modal__td">{row.lastName}</td>
        <td className="obd-atc-modal__td">{row.street}</td>
        <td className="obd-atc-modal__td">{row.city}</td>
        <td className="obd-atc-modal__td">{row.state}</td>
        <td className="obd-atc-modal__td">{row.zipCode}</td>
        <td className="obd-atc-modal__td" style={{ textAlign: 'center' }}>
            {row.titleVerified ? <CheckCircle size={20} color="#10b981" weight="fill" /> : <X size={20} color="#ef4444" weight="fill" />}
        </td>
        {isBK && (
            <>
                <td className="obd-atc-modal__td">{row.useCode}</td>
                <td className="obd-atc-modal__td">{row.useCodeDescription}</td>
                <td className="obd-atc-modal__td">{row.ownerName}</td>
                <td className="obd-atc-modal__td">
                    <Radio.Group 
                        className="obd-atc-modal__radio-group" style={{ whiteSpace: 'nowrap' }}
                        value={row.IsShowContactsOption ? 'update' : 'create'}
                        onChange={(e) => updateRow(index, { 
                            IsUpdate: e.target.value === 'update', 
                            IsShowContactsOption: e.target.value === 'update',
                            OpportunityContactId: e.target.value === 'create' ? null : row.OpportunityContactId 
                        })}
                    >
                        <Radio value="create">Create</Radio>
                        <Radio value="update">Update</Radio>
                    </Radio.Group>
                </td>
                <td className="obd-atc-modal__td">
                    {row.IsShowContactsOption && (
                        <OBDSelect
                            className="obd-atc-modal__select-contact" placeholder="Select contact"
                            options={existingContacts} style={{ width: '100%', minWidth: '180px' }}
                            value={row.OpportunityContactId || undefined}
                            onChange={(v) => updateRow(index, { OpportunityContactId: v })}
                            popupClassName={`obd-select-dropdown obd-theme-${theme}`}
                        />
                    )}
                </td>
            </>
        )}
    </tr>
));

ContactTableRow.displayName = 'ContactTableRow';

export const BlackKnightLeadModal: React.FC<BlackKnightLeadModalProps> = ({ open, setOpen, data, onSuccess }) => {
    const { theme, opportunityDetails: opp, opportunityID: oppId } = useOneButtonStore(); // Destructure store values
    const [dataSource, setDataSource] = useState<any[]>([]); // State for table data
    const [loading, setLoading] = useState(false); // Loading state
    const [isBK, setIsBK] = useState(false); // Determines if we're in Search mode or view mode
    const [syncing, setSyncing] = useState(false); // Loading state for sync
    const [noticeAPI, contextHolder] = notification.useNotification(); // Notification hook
    const oppRef = useRef(opp);
    oppRef.current = opp;
    const fetchOnceRef = useRef(false);

    // Compute dropdown list from current customers
    const contactList = useMemo(() => (opp?.Customers || [])
        .map((c: any) => {
            const id = opportunityContactNumericId(c);
            if (id == null) return null;
            return { label: `${c.FirstName} ${c.LastName}`, value: String(id) };
        })
        .filter((row): row is { label: string; value: string } => row != null), [opp]);

    // Validation check: update requires a selected contact
    const hasError = useMemo(() => dataSource.some(r => r.IsShowContactsOption && !r.OpportunityContactId), [dataSource]);

    // Simplified notification helper
    const notify = useCallback((type: any, msg: string, desc?: string) => {
        noticeAPI[type]({ message: msg, description: desc, placement: 'topRight', className: `obd-notification obd-theme-${theme}` });
    }, [noticeAPI, theme]);

    const mapSyncedCustomers = (customers: any[]) =>
        customers
            .filter((ele: any) => ele?.BKTitleCheckMade === true || ele?.bkTitleCheckMade === true)
            .map((ele: any) => ({
                firstName: ele.FirstName, lastName: ele.LastName,
                street: ele.InsStreet, city: ele.InsCity, state: ele.InsState, zipCode: ele.InsZipCode,
                titleVerified: true, useCode: '', useCodeDescription: '', ownerName: '',
            }));

    const mapSearchOwners = (raw: unknown) =>
        extractTitleSearchOwners(raw).map((ele: any) => ({
            firstName: ele.FirstName || ele.firstName || '',
            lastName: ele.LastName || ele.lastName || '',
            street: ele.Address || ele.street || '',
            city: ele.City || ele.city || '',
            state: ele.State || ele.state || '',
            zipCode: ele.ZIP || ele.zipCode || '',
            titleVerified: true,
            useCode: toTitleUseCode(ele.UseCode ?? ele.useCode),
            useCodeDescription: ele.UseCodeDescription || ele.useCodeDescription || '',
            ownerName: ele.Owner || ele.ownerName || ele.owner || '',
            IsUpdate: false,
            OpportunityContactId: null,
            IsShowContactsOption: false,
        }));

    // Fetch or map data based on synced status
    const fetchData = useCallback(async () => {
        const currentOpp = oppRef.current;
        if (!oppId || !currentOpp) {
            setIsBK(true);
            setDataSource([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const syncedRes = await opportunityInfoServices
                .getIsBlackKnightSync(String(oppId))
                .catch(() => null);

            if (isBlackKnightSyncedResponse(syncedRes)) {
                const customers =
                    Array.isArray(data) && data.length > 0 ? data : currentOpp.Customers || [];
                const dataRes = mapSyncedCustomers(customers);
                if (dataRes.length > 0) {
                    setIsBK(false);
                    setDataSource(dataRes);
                    return;
                }
            }

            // GetBlackKnightSynced returned null/false — search immediately.
            setIsBK(true);
            const res = await opportunityInfoServices.RetrieveBlackKnightPropertySearch({
                address: currentOpp.InstallationAddress?.Street || null,
                lastLine:
                    currentOpp.InstallationAddress?.ZipCode ||
                    currentOpp.InstallationAddress?.zipCode ||
                    null,
                opportunityId: oppId,
                customers: currentOpp.Customers || [],
            });
            setDataSource(mapSearchOwners(res?.data ?? res));
        } catch (err) {
            notify('error', 'Error', 'Black Knight Title check failed, please try again');
            setIsBK(true);
            setDataSource([]);
        } finally { setLoading(false); }
    }, [oppId, data, notify]);

    useEffect(() => {
        if (!open) {
            fetchOnceRef.current = false;
            setDataSource([]);
            return;
        }
        if (fetchOnceRef.current) return;
        fetchOnceRef.current = true;
        void fetchData();
    }, [open, fetchData]);

    // Single handler for row state updates
    const updateRow = useCallback((idx: number, updates: any) => {
        setDataSource(prev => prev.map((row, i) => i === idx ? { ...row, ...updates } : row)); // Update specific row with new values
    }, []);

    // Sync marked title contacts back to opportunity
    const handleSync = async () => {
        if (hasError) return; // Prevent sync if there are validation errors
        setSyncing(true); // Start syncing
        try {
            // Map dataSource to API payload format
            const contacts = dataSource.map(e => ({
                FirstName: e.firstName, LastName: e.lastName, Address: e.street, City: e.city, State: e.state, ZIP: e.zipCode,
                titleVerified: true, UseCode: toTitleUseCode(e.useCode), UseCodeDescription: e.useCodeDescription, Owner: e.ownerName,
                IsUpdate: e.IsUpdate, OpportunityContactId: e.OpportunityContactId
            }));
            const res = await opportunityInfoServices.opportunityContactTitleUpdate({ opportunityId: oppId!, contacts });
            if (res === "true" || res === true) {
                await refreshOpportunityAfterTitleSync(
                    dataSource.map((row) => ({
                        opportunityContactId: row.OpportunityContactId,
                        isUpdate: row.IsUpdate,
                        firstName: row.firstName,
                        lastName: row.lastName,
                    })),
                );
                notify('success', 'Success', 'Black Knight Updated successfully');
                onSuccess?.();
                setOpen(false);
            } else notify('error', 'Error', 'Error in Update Black Knight');
        } catch (err) { notify('error', 'Error', aspNetValidationMessage(err, 'An unexpected error occurred.')); }
        finally { setSyncing(false); } // End syncing
    };

    const isBusy = loading || syncing;

    return (
        <DashboardModal
            open={open}
            onClose={() => setOpen(false)}
            title="Add title contacts"
            footer={
                <div className="obd-atc-modal__actions">
                    {hasError && (
                        <div style={{ color: '#ef4444', fontSize: '13px', marginRight: 'auto', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 500 }}>
                            <Warning size={16} weight="fill" />
                            <span>You have chosen to Update Contact, please choose a contact from the drop down list</span>
                        </div>
                    )}
                    <div className="obd-atc-modal__actions-right">
                        <button 
                            type="button" 
                            className="obd-atc-modal__btn obd-atc-modal__btn--cancel" 
                            onClick={() => setOpen(false)} 
                            disabled={syncing}
                        >
                            <X size={14} weight="bold" />
                            Close
                        </button>
                        <button 
                            type="button" 
                            className="obd-atc-modal__btn obd-atc-modal__btn--primary" 
                            onClick={handleSync} 
                            disabled={syncing || hasError || dataSource.length === 0}
                        >
                            <ArrowsClockwise size={14} weight="bold" />
                            Sync Contacts on Title
                        </button>
                    </div>
                </div>
            }
            width={isBK ? "95vw" : "70vw"}
            theme={theme as 'light' | 'dark'}
            className={`obd-add-title-contacts-modal obd-theme-${theme}`}
            rootClassName={`obd-add-title-contacts-modal-root obd-theme-${theme}`}
            maskClosable={false}
            destroyOnClose
            loading={isBusy}
            loadingMessage={loading ? 'Retrieving data...' : 'Syncing...'}
        >
            <div className="obd-atc-modal__inner" style={{ position: 'relative' }}>
                {contextHolder}

                <div className="obd-atc-modal__body">
                    <div className="obd-atc-modal__table-container">
                        <table className="obd-atc-modal__table">
                            <thead>
                                <tr>
                                    <th className="obd-atc-modal__th">First Name</th>
                                    <th className="obd-atc-modal__th">Last Name</th>
                                    <th className="obd-atc-modal__th">Address</th>
                                    <th className="obd-atc-modal__th">City</th>
                                    <th className="obd-atc-modal__th">State</th>
                                    <th className="obd-atc-modal__th">Zip</th>
                                    <th className="obd-atc-modal__th" style={{ textAlign: 'center' }}>Title Verified</th>
                                    {isBK && (
                                        <>
                                            <th className="obd-atc-modal__th">Use Code</th>
                                            <th className="obd-atc-modal__th">Use Code Description</th>
                                            <th className="obd-atc-modal__th">Owner</th>
                                            <th className="obd-atc-modal__th">Create/Update</th>
                                            <th className="obd-atc-modal__th">Select Contact</th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {dataSource.length === 0 && !loading ? (
                                    <tr>
                                        <td colSpan={isBK ? 12 : 7} className="obd-atc-modal__td" style={{ textAlign: 'center', padding: '32px', opacity: 0.6 }}>
                                            Lead not found in Black Knight.
                                        </td>
                                    </tr>
                                ) : (
                                    dataSource.map((r, i) => (
                                        <ContactTableRow 
                                            key={i} 
                                            row={r} 
                                            theme={theme} 
                                            index={i} 
                                            isBK={isBK} 
                                            existingContacts={contactList} 
                                            updateRow={updateRow} 
                                        />
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

            </div>
        </DashboardModal>
    );
};

export default BlackKnightLeadModal;
